import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../../apiClient';
import { OrganizationCguEnforcement } from '../../../models/organization/dto/organization.dto';

/**
 * GET /organizations/:id/cgu — operator-facing read of the org-level CGU.
 *
 * Returns the actual stored cguText (or null), the current cguVersion, and the
 * current cguEnforcement mode. Distinct from the user-facing
 * /membership-types/:typeId/cgu, which goes through resolveCgu and may return
 * a per-type override instead (and respects the OFF kill-switch).
 */
export const GET_ORG_CGU_QUERY_KEY = (organizationId: string) => [
  'cgu',
  organizationId,
  'org-default',
];

export type OrganizationCguResponse = {
  cguText: string | null;
  cguVersion: number;
  cguEnforcement: OrganizationCguEnforcement;
};

export const useGetOrganizationCgu = (
  organizationId: string | undefined,
  options?: { enabled?: boolean },
) => {
  const apiClient = useApiClient();
  return useQuery({
    queryKey: GET_ORG_CGU_QUERY_KEY(organizationId ?? ''),
    enabled: Boolean(organizationId) && options?.enabled !== false,
    queryFn: () =>
      apiClient.fetchApi<OrganizationCguResponse, {}>({
        method: 'GET',
        endpoint: `organizations/${organizationId}/cgu`,
      }),
  });
};
